import { SocialAuthService } from '@abacritt/angularx-social-login';
import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, EMPTY, Observable, Subject, combineLatest, from, merge, of, startWith, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, finalize, map, mergeMap, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { HttpService } from 'src/app/services/http.service';
import { RoutingService } from '../routing.service';

import { ForgotPasswordDto } from 'src/app/account/forgot-password/forget-password.model';
import { LoginRequest } from 'src/app/account/login/login-request.model';
import { UserType } from 'src/app/account/login/UserType.enum';
import { PersonalDetailsReqRes } from 'src/app/account/models/personalDetailsReqRes.model';
import { AgentDetailsResponse, ClientDetailsResponse } from 'src/app/account/my-account/model/agentDetailsResponse.model';
import { FeatureFlags } from 'src/app/account/my-account/model/FeatureFlags.enum';
import { OrganizationDetailsReqRes } from 'src/app/account/my-account/model/OrganizationDetailsReqRes.model';
import { TwoFactorModel } from 'src/app/account/my-account/model/TwoFactorModel.model';
import { ClientPresentationState } from 'src/app/account/my-account/model/ui-state/ClientPresentationState';
import { VerifyPhoneReqRes } from 'src/app/account/my-account/model/VerifyPhoneReqRes.model';
import { RegisterRequest } from 'src/app/account/register/register-request.model';
import { CompanyProfileLpRequest } from 'src/app/lp/lp-profile/company-profile-lp/companyProfileLpRequest.model';
import { OnboardingLpRequest } from 'src/app/lp/onboarding-lp/onboardingLpRequest.model';
import { LoginResponse } from 'src/app/models/LoginResponse.model';
import { OnboardingRequest } from 'src/app/onboarding/OnboardingRequest.model';
import { UserPermissionDto } from 'src/app/permission/classes/userPermissionDto.models';
import { AppType } from 'src/app/shared/enums/AppType.enum';
import { LoggerService } from 'src/app/shared/errors/logger.service';
import { OrganizationReferrerRequest, OrganizationReferrerResponse } from 'src/app/shared/models/organization-referrer-request.model';
import { RoleFlags } from '../../account/my-account/model/RoleFlags.enum';
import { InvitedUserUpdateDto } from '../../account/register/InvitedUserUpdateDto';
import { OrganizationInvitationReadDto } from '../../account/register/OrganizationInvitationReadDto.model';
import { AppSettingsService, SubdomainEnum } from '../app-settings.service';
import { StorageService } from '../storage.service';


@Injectable({
  providedIn: 'root'
})
export class UserService {
  private AUTH_CLIENT_ID = 'covercyNgTerra';
  private LP_CLIENT_ID = 'covercyNgLpApp';
  private MAINTENANCE_URL = this.routingService.maintenancePage;

  public readonly ACCOUNT_ENDPOINT = 'account';
  private ONBOARDING_ENDPOINT = 'onboarding';

  private SIGNUP_GP_ENDPOINT = this.ACCOUNT_ENDPOINT + '/signup/gp';
  private SIGNUP_LP_ENDPOINT = this.ACCOUNT_ENDPOINT + '/signup';

  private LOGIN_GP_ENDPOINT = this.ACCOUNT_ENDPOINT + '/login/gp';
  private LOGIN_LP_ENDPOINT = this.ACCOUNT_ENDPOINT + '/login';

  private LOGOUT_ENDPOINT = this.ACCOUNT_ENDPOINT + '/logout';
  private FORGOT_PASSWORD_ENDPOINT = this.ACCOUNT_ENDPOINT + '/forgotPassword';
  private RESET_PASSWORD_ENDPOINT = this.ACCOUNT_ENDPOINT + '/resetPassword';
  private GET_CURRENT_USER_ENDPOINT = this.ACCOUNT_ENDPOINT + '/currentUser';

  private CURRENT_ORGANIZATION_MEMBERS_ENDPOINT = this.ACCOUNT_ENDPOINT + '/organization-members';

  private COMPANY_PROFILE_GP_ENDPOINT = this.ACCOUNT_ENDPOINT + '/companyProfile/gp';
  private COMPANY_PROFILE_LP_ENDPOINT = this.ACCOUNT_ENDPOINT + '/companyProfile/lp';

  private ONBOARDING_LP_ENDPOINT = this.ACCOUNT_ENDPOINT + '/lp/onboarding';

  private PERSONAL_DETAILS_AGENT_ENDPOINT = this.ACCOUNT_ENDPOINT + '/details/gp';
  private PERSONAL_DETAILS_CLIENT_ENDPOINT = this.ACCOUNT_ENDPOINT + '/details/lp';

  private RESEND_CONFIRMATION_ENDPOINT = this.ACCOUNT_ENDPOINT + '/resendConfirmationEmail';
  private CONFIRM_EMAIL_ENDPOINT = this.ACCOUNT_ENDPOINT + '/confirmEmail';
  private REJECT_EMAIL_ENDPOINT = this.ACCOUNT_ENDPOINT + '/rejectEmail';

  private EXTERNAL_REGISTER = this.ACCOUNT_ENDPOINT + '/external-register';
  private EXTERNAL_OBTAIN_ACCESSTOKEN = this.ACCOUNT_ENDPOINT + '/obtain-local-accesstoken';

  private CROSS_APP_LOGIN = this.ACCOUNT_ENDPOINT + '/cross-app-login';

  private SEND_SMS_VERIFICATION = this.ACCOUNT_ENDPOINT + '/sms/send-verification';
  private SMS_VERIFY = this.ACCOUNT_ENDPOINT + '/sms/verify';
  private RESEND_SMS_VERIFICATION = this.ACCOUNT_ENDPOINT + '/sms/resend';

  private TWO_FACTOR_PING = this.ACCOUNT_ENDPOINT + '/2fa/ping';
  private TWO_FACTOR_ENABLE = this.ACCOUNT_ENDPOINT + '/2fa/enable';
  private TWO_FACTOR_DISABLE = this.ACCOUNT_ENDPOINT + '/2fa/disable';
  private TWO_FACTOR_VERIFY = this.ACCOUNT_ENDPOINT + '/2fa/verify';
  private TWO_FACTOR_CODE = this.ACCOUNT_ENDPOINT + '/2fa/code';
  private TWO_FACTOR_CODE_RESEND = this.ACCOUNT_ENDPOINT + '/2fa/code-resend';

  private PENDING_PAYMENT_REQUESTS = 'paymentRequest';
  private UPDATE_TERMS_AND_CONDITIONS_ENDPOINT = this.ACCOUNT_ENDPOINT + '/update-terms-and-conditions';
  private ADD_TEAM_MEMBER_ENDPOINT = this.ACCOUNT_ENDPOINT + '/add-team-member';
  private INVITATION_INFO_ENDPOINT = this.ACCOUNT_ENDPOINT + '/invite';
  private ADD_LP_FOR_IMPERSONATION_ENDPOINT = this.ACCOUNT_ENDPOINT + '/add-lp-for-impersonation';

  private REFERER_LOGO_ENDPOINT = this.ACCOUNT_ENDPOINT + '/referrer-logo';

  private authHeaders: Headers;

  // Used to compare to the clientDetails in local storage, to tell if the user is logged in on another tab as a different user.
  private clientDetails: AgentDetailsResponse;

  isLoggedIn$ = new BehaviorSubject<boolean>(this.isLoggedIn());

  // private lp_referrerFromSession$ = new BehaviorSubject<OrganizationReferrerResponse>(this.getReferrerFromSession(this.appSettingsService.lpReferrerCookieKey));
  private lp_referrerFromCookie$ = new BehaviorSubject<OrganizationReferrerResponse>(this.getReferrerFromCookie(this.appSettingsService.lpReferrerCookieKey));
  private lp_referrerFromQuery$: Observable<string> = this.activatedRoute.queryParamMap.pipe(
    map(queryParams => queryParams?.get(this.appSettingsService.lpReferrerCookieKey))
  );

  private gp_referrerFromSession$ = new BehaviorSubject<OrganizationReferrerResponse>(this.getReferrerFromSession(this.appSettingsService.gpReferrerCookieKey));
  public gp_referrerFromCookie$ = new BehaviorSubject<OrganizationReferrerResponse>(this.getReferrerFromCookie(this.appSettingsService.gpReferrerCookieKey));
  public gp_referrerFromQuery$: Observable<string> = this.activatedRoute.queryParamMap.pipe(
    map(queryParams => queryParams?.get(this.appSettingsService.gpReferrerCookieKey))
  );

  lp_referrerLogo$: Observable<OrganizationReferrerResponse> = combineLatest([this.lp_referrerFromQuery$.pipe(startWith(null)), this.lp_referrerFromCookie$])
    .pipe(
      switchMap(([referrerFromQuery, referrerFromCookie]) => {
        if (referrerFromQuery) {
          this.storageService.setGpReferralKeyDataInStorage(referrerFromQuery);
          return this.getLogoByReferrer(referrerFromQuery)
            .pipe(tap(data => {
              this.setReferrerInCookie(this.appSettingsService.lpReferrerCookieKey, data);
            }));
        }
        // else if (referrerFromSession){
        //   return (referrerFromSession && referrerFromSession?.showLogo) ? of(referrerFromSession) : of(null);
        // }
        else if (referrerFromCookie) {
          return (referrerFromCookie && referrerFromCookie?.showLogo) ? of(referrerFromCookie) : of(null);
        } else {
          return of(null);
        }
      }),
      shareReplay(1));

  gp_referrerLogo$: Observable<OrganizationReferrerResponse> = combineLatest([this.gp_referrerFromQuery$.pipe(startWith(null)), this.gp_referrerFromCookie$])
    .pipe(
      switchMap(([referrerFromQuery, referrerFromCookie]) => {
        if (referrerFromQuery) {
          return this.getLogoByReferrer(referrerFromQuery)
            .pipe(
              tap(data => {
                this.setReferrerInCookie(this.appSettingsService.gpReferrerCookieKey, data);
              }));
        }
        // else if (referrerFromSession){
        //   return (referrerFromSession && referrerFromSession?.showLogo) ? of(referrerFromSession) : of(null);
        // }
        else if (referrerFromCookie) {
          return (referrerFromCookie && referrerFromCookie?.showLogo) ? of(referrerFromCookie) : of(null);
        } else {
          return of(null);
        }
      }),
      shareReplay(1));

  refreshedClientDetails$ = new BehaviorSubject<ClientDetailsResponse>(null);

  accountDetails$: Observable<ClientDetailsResponse> =
    this.isLoggedIn$.pipe(
      switchMap(isLoggedIn => {
        // In case the user is not logged in, we don't want to attempt getting the clientDetails, because it will fail (there is no client)
        if (!isLoggedIn) {
          return EMPTY;
        }
        // Otherwise, get the client details either from the refreshed client details object, or from the server.
        return this.refreshedClientDetails$;
      }
      ),
      switchMap(refreshedClientDetails => refreshedClientDetails ? of(refreshedClientDetails) : this.getClientDetailsFromServer()),
      shareReplay(1)
    );

  private supportPinFromServer$ = this.http.getTyped<string>(this.ACCOUNT_ENDPOINT + '/support-pin').pipe(shareReplay(1));
  private supportPinUpdated$ = new Subject<string>();
  supportPin$ = merge(this.supportPinUpdated$, this.supportPinFromServer$).pipe(
    distinctUntilChanged(),
    shareReplay(1));

  private isAnonymousUntilNextNavigationPrivate = false;

  get isAnonymousUntilNextNavigation() {
    return this.isAnonymousUntilNextNavigationPrivate;
  }

  userHasFeatureFlag(featureToCheck: FeatureFlags): Observable<boolean> {
    return this.accountDetails$.pipe(
      map(accountDetails => FeatureFlags.isOn(accountDetails.featureFlags, featureToCheck)),
      shareReplay(1));
  }

  userHasRoleFlag(roleToCheck: RoleFlags): Observable<boolean> {
    return this.accountDetails$.pipe(
      map(accountDetails => RoleFlags.isOn(accountDetails.roleFlags, roleToCheck)),
      shareReplay(1)
    );
  }

  constructor(
    private http: HttpService,
    private routingService: RoutingService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private cookieService: CookieService,
    private appSettingsService: AppSettingsService,
    private storageService: StorageService,
    private socialAuthService: SocialAuthService,
    private logger: LoggerService
  ) {
    this.authHeaders = new Headers();
    this.authHeaders.append('Accept', 'application/json');
    this.authHeaders.append('Content-Type', 'application/x-www-form-urlencoded');

    // user to validate user details against local storage (for logging in another tab for example)
    const clientDetailsFromStorage = this.getClientDetailsFromLocalStorage();

    if (clientDetailsFromStorage && !this.clientDetails) {
      this.getClientDetails().subscribe(response => {
        this.clientDetails = response;
      });
    }
  }

  /**
   * When this flag is set, any failed token refresh attempt will not redirect to the login page.
   */
  public setAnonymousUntilNextNavigation() {
    this.isAnonymousUntilNextNavigationPrivate = true;
    // After the first navigation to another page, stop being anonymous.
    this.router.events.pipe(
        filter(evt => evt instanceof NavigationStart),
        take(1)
      ).subscribe(_ => {
        this.isAnonymousUntilNextNavigationPrivate = false;
      });
  }

  private getClientDetailsFromServer(): Observable<ClientDetailsResponse> {
    return this.http.getTyped<ClientDetailsResponse>(this.ACCOUNT_ENDPOINT + '/details/gp').pipe(
      map(response => {
        // If there is a UI state saved for this client,
        // parse it from a string to a ClientPresentationState object, and replace this property in th the account details
        if (response.clientPresentationState) {
          response.clientPresentationState = ClientPresentationState.fromJson(JSON.parse(response.clientPresentationState.toString()));
        } else {
          response.clientPresentationState = new ClientPresentationState();
        }

        this.storageService.setCredentialsData(JSON.stringify(response));
        return response;
      }),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  private getCurrentUser() {
    return this.http.getTyped<AgentDetailsResponse>(this.GET_CURRENT_USER_ENDPOINT).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  getClientDetailsFromLocalStorage(): AgentDetailsResponse {
    const credentialsData = this.storageService.getCredentialsData();
    if (credentialsData && credentialsData.firstName) {
      return credentialsData as AgentDetailsResponse;
    }
    return null;
  }

  getClientDetails(forceFromServer = false): Observable<AgentDetailsResponse> {
    const clientDetails = this.getClientDetailsFromLocalStorage();

    if (!forceFromServer && clientDetails && clientDetails.firstName) {
      return of(clientDetails);
    } else {
      return this.getCurrentUser().pipe(
        map((response: any) => {

          this.storageService.setCredentialsData(JSON.stringify(response));
          return response;
        }),
        catchError(err => {
          return throwError(err);
        })
      );
    }
  }

  saveSupportPIN(pin: string): Observable<any> {
    const url = this.ACCOUNT_ENDPOINT + '/support-pin';
    return this.http.put(url, pin).pipe(tap(() => {
      this.supportPinUpdated$.next(pin);
    }));
  }

  changePassword(oldPassword: string, password: string, confirmPassword: string): Observable<any> {
    const url = this.ACCOUNT_ENDPOINT + '/changePassword';

    const model = {
      oldPassword,
      password,
      confirmPassword
    };

    return this.http.post(url, model);
  }

  changeEmail(email: string, password: string, recaptcha: string): Observable<any> {
    const url = this.ACCOUNT_ENDPOINT + '/changeEmail';
    const model = {
      email,
      password,
      recaptchaToken: recaptcha
    };
    return this.http.post(url, model).pipe(tap(response => {
      this.storageService.setAuthorizationData(JSON.stringify(response));
      this.refreshedClientDetails$.next(null);
    }));
  }

  register(signupModel: RegisterRequest): Observable<LoginResponse> {
    const registerEndpoint = signupModel.appType === AppType.GpApp ? this.SIGNUP_GP_ENDPOINT : this.SIGNUP_LP_ENDPOINT;
    return this.http.postTyped<LoginResponse>(registerEndpoint, signupModel)
      .pipe(
        map(response => {
          if (response.token) {
            this.storageService.setAuthorizationData(JSON.stringify(response.token));
            this.storageService.setCredentialsData(JSON.stringify(response.clientDetails));
            this.isLoggedIn$.next(true);
          }
          return response;
        }),
        tap(() => {
          this.refreshedClientDetails$.next(null);
        }),
        catchError(err => {
          this.storageService.clear();

          if (err.status === 503) {
            this.router.navigateByUrl(this.MAINTENANCE_URL);
          }
          return throwError(err);
        })
      );
  }

  registerLp(signupModel: RegisterRequest, doNotLogin: boolean = false): Observable<LoginResponse> {
    return this.http.postTyped<LoginResponse>(this.SIGNUP_LP_ENDPOINT, signupModel)
      .pipe(
        map(response => {
          if (response.token) {
            this.storageService.setAuthorizationData(JSON.stringify(response.token));
            this.storageService.setCredentialsData(JSON.stringify(response.clientDetails));
            this.isLoggedIn$.next(true);
          }
          return response;
        }),
        tap(() => {
          this.refreshedClientDetails$.next(null);
        }),
        catchError(err => {
          this.storageService.clear();

          if (err.status === 503) {
            this.router.navigateByUrl(this.MAINTENANCE_URL);
          }
          return throwError(err);
        })
      );
  }

  logout() {
    this.isLoggedIn$.next(false);
    return this.ssoLogout()
      .pipe(
        mergeMap(_ => {
          return this.http.post(this.LOGOUT_ENDPOINT, null);
        }),
        catchError(err => {
          this.logger.error(`Error in UserService => Logout()`, err);
          console.error('Error:', err);
          return of(null);
        }),
        finalize(() => {
          this.storageService.clear();
          this.routingService.removeReturnUrlFromCookie();
        })
      );
  }

  login(loginRequest: LoginRequest): Observable<LoginResponse> {
    const loginEndpoint = loginRequest.appType === AppType.GpApp ? this.LOGIN_GP_ENDPOINT : this.LOGIN_LP_ENDPOINT;
    return this.http.postTyped<LoginResponse>(loginEndpoint, loginRequest).pipe(
      map(response => {

        // reset all previous data of local storage
        // this.storageService.clear();
        if (response.token) {
          this.storageService.setAuthorizationData(JSON.stringify(response.token));
          this.storageService.setCredentialsData(JSON.stringify(response.clientDetails));
          this.isLoggedIn$.next(true);
        }
        return response;
      }),
      // tap(() => this.isLoggedIn$.next(true)),
      tap(() => {
        this.refreshedClientDetails$.next(null);
      }),
      catchError(err => {

        this.storageService.clear();

        if (err.status === 503) {
          this.router.navigateByUrl(this.MAINTENANCE_URL);
        }
        return throwError(err);
      })
    );
  }

  isTryingToAccessOtherUsersData() {
    const storageUserDetails = this.getClientDetailsFromLocalStorage();
    if (storageUserDetails && this.clientDetails && storageUserDetails.id !== this.clientDetails.id) {
      return true;
    }
    return false;
  }

  private ssoLogout() {
    try {
      return this.socialAuthService.initState.pipe(
        filter(isReady => isReady),
        take(1),
        switchMap(() => {
          return from(this.socialAuthService.signOut()).pipe(
            catchError(error => {
              this.logger.warning('SSO Logout error', error);
              console.error('SSO Logout error', error);
              return of(null);
            })
          );
        })
      );
    } catch (err) {
      this.logger.error(`Exception in UserService => ssoLogout()`, err);
      return of(null);
    }
  }

  private validateLocalStorageCredentials() {
    const result = this.storageService.getAuthorizationData();

    if (result === null) {
      return false;
    }
    return true;
  }

  isLoggedIn() {
    return this.validateLocalStorageCredentials();
  }

  public getReferrerFromCookie(referrerKey): OrganizationReferrerResponse {

    try {
      var value = this.cookieService.get(referrerKey);

      if (value) {
        var referrerData = JSON.parse(value) as OrganizationReferrerResponse;
        return referrerData;
      }

      return null;
    } catch (error) {
      console.log('could not parse obj from cookie. the value is either null or not properly constructed.');
      return null;
    }

  }

  public setReferrerInCookie(referrerKey: string, referrerValue: OrganizationReferrerResponse) {

    if (!referrerKey || !referrerValue) return;

    const date = new Date();

    date.setDate(date.getFullYear() + 1);

    this.cookieService.delete(referrerKey, '/', location.hostname);

    if (referrerValue.showLogo && !!referrerValue.logoUrl) {
      this.cookieService.set(referrerKey, JSON.stringify(referrerValue), date, '/', location.hostname);
    }

  }

  public getReferrerFromSession(referrerKey) {

    try {
      var value = sessionStorage.getItem(referrerKey);

      if (value) {
        var referrerData = JSON.parse(value) as OrganizationReferrerResponse;
        return referrerData;
      }

      return null;
    } catch (error) {
      console.log('could not parse obj from cookie. the value is either null or not properly constructed.');
      return null;
    }


  }

  public setReferrerInSession(referrerKey: string, referrerValue: OrganizationReferrerResponse) {


    if (!referrerKey || !referrerValue) return;

    const date = new Date();

    date.setDate(date.getFullYear() + 1);

    sessionStorage.setItem(referrerKey, JSON.stringify(referrerValue));
  }

  forgotPassword(forgotPasswordDto: ForgotPasswordDto): Observable<any> {
    return this.http.post(this.FORGOT_PASSWORD_ENDPOINT, forgotPasswordDto);
  }

  onboardingSubmit(onboardingModel: OnboardingRequest) {
    return this.http.post(this.ONBOARDING_ENDPOINT, onboardingModel).pipe(
      switchMap(() => this.getClientDetailsFromServer()),
      tap(clientDetails => this.refreshedClientDetails$.next(clientDetails)),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  createExampleHoldings() {
    return this.http.post(`${this.ONBOARDING_ENDPOINT}/example-holding`, null).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  resetPassword(token: string, password: string, confirmPassword: string) {
    const data = {token, password, confirmPassword};

    return this.http.post(this.RESET_PASSWORD_ENDPOINT, data);
  }

  removeUserCredentials() {
    this.storageService.removeCredentialsData();
  }

  companyProfileGpUpdate(request: OrganizationDetailsReqRes): Observable<OrganizationDetailsReqRes> {
    return this.http.postTyped<OrganizationDetailsReqRes>(this.COMPANY_PROFILE_GP_ENDPOINT, request).pipe(
      tap(() => this.removeUserCredentials()),
      tap(() => this.refreshedClientDetails$.next(null)),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  companyProfileLpUpdate(request: CompanyProfileLpRequest): Observable<CompanyProfileLpRequest> {
    return this.http.postTyped<CompanyProfileLpRequest>(this.COMPANY_PROFILE_LP_ENDPOINT, request).pipe(
      tap(() => this.removeUserCredentials()),
      tap(() => this.refreshedClientDetails$.next(null)),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  // We can make the assumtion that GPs are always Agents, and LP are Clients
  personalDetailsUpdate(request: PersonalDetailsReqRes, userType: UserType): Observable<PersonalDetailsReqRes> {
    // different endpoints for LP and GP
    const endpoint = userType === UserType.Agent ? this.PERSONAL_DETAILS_AGENT_ENDPOINT : this.PERSONAL_DETAILS_CLIENT_ENDPOINT;

    return this.http.postTyped<PersonalDetailsReqRes>(endpoint, request).pipe(
      tap(() => this.removeUserCredentials()),
      tap(() => this.refreshedClientDetails$.next(null)),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  resendConfirmationEmail(overrideCounter: boolean) {

    const queryString = `?overrideCounter=${overrideCounter}`;

    return this.http.get(this.RESEND_CONFIRMATION_ENDPOINT + queryString).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  confirmEmail(username: string, token: string) {
    const data = {username, token};

    return this.http.put(this.CONFIRM_EMAIL_ENDPOINT, data).pipe(
      tap(() => this.refreshedClientDetails$.next(null)),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  rejectEmail(username: string, token: string) {
    const data = {username, token};

    return this.http.put(this.REJECT_EMAIL_ENDPOINT, data).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  registerGoogle(signupModel: LoginRequest): Observable<any> {
    return this.externalRegister('Google', signupModel);
  }

  registerFacebook(signupModel: LoginRequest): Observable<any> {
    return this.externalRegister('Facebook', signupModel);
  }

  private externalRegister(provider: string, signupModel: LoginRequest) {

    signupModel.provider = provider;

    return this.http
      .post(this.EXTERNAL_REGISTER, signupModel)
      .pipe(
        map((response: any) => {

          // this.storageService.clear();

          this.storageService.setAuthorizationData(JSON.stringify(response.token));
          this.storageService.setCredentialsData(JSON.stringify(response.clientDetails));

          return response;
        }),
        catchError(err => {
          if (err.status === 503) {
            this.router.navigateByUrl(this.MAINTENANCE_URL);
          }
          return throwError(err);
        })
      );
  }

  obtainLocalAccessToken(loginModel: LoginRequest) {

    const endpoint = this.EXTERNAL_OBTAIN_ACCESSTOKEN;

    return this.http
      .post(endpoint, loginModel)
      .pipe(
        map((response: any) => {

          // this.storageService.clear();

          if (response.token) {
            this.storageService.setAuthorizationData(JSON.stringify(response.token));
            this.storageService.setCredentialsData(JSON.stringify(response.clientDetails));
            this.isLoggedIn$.next(true);
          }
          return response;
        }),
        // tap(() => this.isLoggedIn$.next(true)),
        tap(() => {
          this.refreshedClientDetails$.next(null);
        }),
        catchError(err => {
          if (err.status === 503) {
            this.router.navigateByUrl(this.MAINTENANCE_URL);
          }
          return throwError(err);
        })
      );
  }

  crossAppLogin() {
    const endpoint = this.CROSS_APP_LOGIN + `?clientId=${this.appSettingsService.subDomain === SubdomainEnum.LP ? this.LP_CLIENT_ID : null}`;

    return this.http
      .get(endpoint)
      .pipe(
        map((response: any) => {
          // reset all previous data of local storage
          // this.storageService.clear();

          // when cross app login doesn't work for legitmiate reasons (status 204):
          if (!response) {
            return null;
          }

          if (response.token) {
            this.storageService.setAuthorizationData(JSON.stringify(response.token));
            this.storageService.setCredentialsData(JSON.stringify(response.clientDetails));
            this.isLoggedIn$.next(true);
            return response;
          } else {
            return null;
          }
        }),
        tap(() => {
          this.refreshedClientDetails$.next(null);
        }),
        catchError(err => {
          if (err.status === 503) {
            this.router.navigateByUrl(this.MAINTENANCE_URL);
          }
          return throwError(err);
        })
      );
  }

  sendSmsVerification(model: VerifyPhoneReqRes): Observable<any> {
    const url = this.SEND_SMS_VERIFICATION;

    return this.http.putTyped<VerifyPhoneReqRes>(url, model);
  }

  smsVerify(model: VerifyPhoneReqRes): Observable<any> {
    const url = this.SMS_VERIFY;

    return this.http.putTyped<VerifyPhoneReqRes>(url, model);
  }

  reSendSmsVerification(): Observable<any> {
    const url = this.RESEND_SMS_VERIFICATION;

    return this.http.putTyped<VerifyPhoneReqRes>(url, null);
  }

  private setTwoFactorCookie(twoFactorModel: TwoFactorModel) {
    const date = new Date();

    if (twoFactorModel.rememberDevice) {
      date.setDate(date.getDate() + 30);
    } else {
      date.setHours(date.getHours() + 1);
    }
    this.cookieService.set('covercy_device_id', twoFactorModel.deviceToken, date, '/', this.appSettingsService.cookieDomainNamespace, true);
  }

  twoFactorEnable(): Observable<any> {
    const url = this.TWO_FACTOR_ENABLE;

    return this.http.getTyped<TwoFactorModel>(url).pipe(map((response: TwoFactorModel) => {
      this.setTwoFactorCookie(response);
      return response;
    }));
  }

  twoFactorPing() {
    return this.http.getTyped<TwoFactorModel>(this.TWO_FACTOR_PING);
  }

  twoFactorDisable(): Observable<any> {
    const url = this.TWO_FACTOR_DISABLE;

    return this.http.getTyped<TwoFactorModel>(url);
  }

  twoFactorVerify(twoFactorModel: TwoFactorModel): Observable<any> {
    const url = this.TWO_FACTOR_VERIFY;

    return this.http.postTyped<TwoFactorModel>(url, twoFactorModel).pipe(map((response: TwoFactorModel) => {
      this.setTwoFactorCookie(response);
      return response;
    }));
  }

  sendTwoFactorCode(): Observable<any> {
    const url = this.TWO_FACTOR_CODE;
    return this.http.get(url);
  }

  resendTwoFactorCode(): Observable<any> {
    const url = this.TWO_FACTOR_CODE_RESEND;

    return this.http.get(url);
  }

  onboardingLp(request: OnboardingLpRequest): Observable<ClientDetailsResponse> {
    return this.http.postTyped<ClientDetailsResponse>(this.ONBOARDING_LP_ENDPOINT, request).pipe(
      tap(clientDetails => {
        this.removeUserCredentials();
        this.refreshedClientDetails$.next(clientDetails);
      }),
      catchError(err => throwError(() => err)));
  }

  getCurrentAgentOrganizationMembers(): Observable<ClientDetailsResponse[]> {
    return this.http.getTyped<ClientDetailsResponse[]>(this.CURRENT_ORGANIZATION_MEMBERS_ENDPOINT).pipe(
      catchError(err => throwError(() => err)));
  }

  updateTermsAndConditions(): Observable<boolean> {
    return this.http.patchTyped<boolean>(this.UPDATE_TERMS_AND_CONDITIONS_ENDPOINT, true).pipe(
      catchError(err => throwError(() => err)));
  }

  getOrganizationInvitationInfo(invitationToken: string): Observable<OrganizationInvitationReadDto> {
    return this.http.getTyped<OrganizationInvitationReadDto>(`${this.INVITATION_INFO_ENDPOINT}/${invitationToken}`).pipe(
      catchError(err => throwError(() => err)));
  }


  getLogoByReferrer(referrerKey: string = null): Observable<OrganizationReferrerResponse> {
    const referrerReq = {
      referrerKey
    } as OrganizationReferrerRequest;
    return this.http.postTyped<OrganizationReferrerResponse>(this.REFERER_LOGO_ENDPOINT, referrerReq)
      .pipe(catchError(err => throwError(() => err)));
  }

  updateInvitedUserInfo(invitationToken: string, userInfo: InvitedUserUpdateDto): Observable<LoginResponse> {
    return this.http.putTyped<any>(`${this.INVITATION_INFO_ENDPOINT}/${invitationToken}/user`, userInfo).pipe(
      map(response => {
        if (response.token) {
          this.storageService.setAuthorizationData(JSON.stringify(response.token));
          this.storageService.setCredentialsData(JSON.stringify(response.clientDetails));
          this.isLoggedIn$.next(true);
        }
        return response;
      }),
      tap(() => {
        this.refreshedClientDetails$.next(null);
      }),
      catchError(err => {
        this.storageService.clear();

        if (err.status === 503) {
          this.router.navigateByUrl(this.MAINTENANCE_URL);
        }
        return throwError(err);
      })
    );
  }

  addLpForImpersonation(email: string) {
    return this.http.postTyped(this.ADD_LP_FOR_IMPERSONATION_ENDPOINT, email)
      .pipe(catchError(err => throwError(() => err)));
  }

  // ############## start manage users ##############

  createUserPermission(userPermission: UserPermissionDto): Observable<UserPermissionDto> {
    return this.http.postTyped<UserPermissionDto>(this.ADD_TEAM_MEMBER_ENDPOINT, userPermission)
      .pipe(catchError(err => throwError(() => err)));
  }

  // ############## end manage users ##############

}
